(function () {
  const FREE_GIFT_VARIANT_ID = 47137431781629;
  const THRESHOLD = 10000; // $100.00 in cents

  let isProcessing = false;

  // ─── Read total directly from cart response body ──────────────────────────
  // Shopify's /cart/add.js and /cart/change.js both return the full cart state
  // We extract total_price from it — no second fetch needed

  function getTotalExcludingGift(cartData) {
    if (!cartData || !cartData.items) return 0;
    return cartData.items.reduce((sum, item) => {
      if (item.variant_id === FREE_GIFT_VARIANT_ID) return sum;
      return sum + item.final_line_price;
    }, 0);
  }

  function hasFreeGift(cartData) {
    return cartData?.items?.some(i => i.variant_id === FREE_GIFT_VARIANT_ID) || false;
  }

  function getFreeGiftKey(cartData) {
    return cartData?.items?.find(i => i.variant_id === FREE_GIFT_VARIANT_ID)?.key || null;
  }

  // ─── Cart mutations ───────────────────────────────────────────────────────

  function addFreeGift() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: FREE_GIFT_VARIANT_ID, quantity: 1, properties: { _free_gift: 'true' } }]
      })
    }).then(r => {
      if (!r.ok) return r.json().then(e => console.error('[FreeGift] Add failed:', e.description || e));
      return r.json();
    });
  }

  function removeFreeGift(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    }).then(r => r.json());
  }

  // ─── Trigger theme cart UI refresh ───────────────────────────────────────

  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('theme:cart:update'));
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents();
  }

  // ─── Core decision — called with already-fetched cart data ───────────────
  // No extra fetch here. cartData comes straight from the mutation response.

  function handleCartData(cartData) {
    if (!cartData || isProcessing) return;
    isProcessing = true;

    const total   = getTotalExcludingGift(cartData);
    const hasGift = hasFreeGift(cartData);
    const giftKey = getFreeGiftKey(cartData);

    if (total >= THRESHOLD && !hasGift) {
      addFreeGift()
        .then(() => refreshCartUI())
        .finally(() => { isProcessing = false; });

    } else if (total < THRESHOLD && hasGift) {
      removeFreeGift(giftKey)
        .then(() => refreshCartUI())
        .finally(() => { isProcessing = false; });

    } else {
      isProcessing = false;
    }
  }

  // ─── Fetch interceptor ────────────────────────────────────────────────────
  // Intercepts /cart/add.js and /cart/change.js responses FROM THE THEME
  // Reads the returned cart JSON directly — zero extra network call

  const _fetch = window.fetch;
  window.fetch = function (...args) {
    const url  = (args[0] instanceof Request ? args[0].url : String(args[0] || ''));
    const body = String(args[1]?.body || '');

    // Skip our own gift add/remove calls to prevent infinite loop
    if (body.includes(String(FREE_GIFT_VARIANT_ID))) {
      return _fetch.apply(this, args);
    }

    const isAdd    = url.includes('/cart/add');
    const isChange = url.includes('/cart/change') || url.includes('/cart/update');
    const isClear  = url.includes('/cart/clear');

    if (!isAdd && !isChange && !isClear) {
      return _fetch.apply(this, args);
    }

    return _fetch.apply(this, args).then(response => {
      const clone = response.clone();

      if (isClear) {
        // Cart cleared — nothing to do, gift is gone too
        isProcessing = false;
        return response;
      }

      clone.json().then(cartData => {
        // /cart/add.js returns { items: [...] } (just added items, NOT full cart)
        // /cart/change.js and /cart/update.js return the FULL cart
        if (isChange) {
          // Full cart — use directly
          handleCartData(cartData);
        } else if (isAdd) {
          // /cart/add.js only returns added items — fetch full cart once
          // BUT do it immediately (no setTimeout) so it's fast
          _fetch('/cart.js')
            .then(r => r.json())
            .then(fullCart => handleCartData(fullCart))
            .catch(e => { isProcessing = false; console.error('[FreeGift]', e); });
        }
      }).catch(e => {
        isProcessing = false;
        console.error('[FreeGift] Response parse error:', e);
      });

      return response;
    });
  };

  // ─── CSS — hide remove button & lock qty using exact theme selectors ──────

  function injectStyles() {
    if (document.getElementById('fg-styles')) return;
    const s = document.createElement('style');
    s.id = 'fg-styles';
    s.textContent = `
      .product-cart-item:has(quantity-selector[data-id^="${FREE_GIFT_VARIANT_ID}"]) a.remove,
      .product-cart-item:has(quantity-selector[data-id^="${FREE_GIFT_VARIANT_ID}"]) .remove {
        display: none !important;
        pointer-events: none !important;
      }
      quantity-selector[data-id^="${FREE_GIFT_VARIANT_ID}"] button.minus,
      quantity-selector[data-id^="${FREE_GIFT_VARIANT_ID}"] button.plus,
      quantity-selector[data-id^="${FREE_GIFT_VARIANT_ID}"] input.qty {
        pointer-events: none !important;
        opacity: 0.4 !important;
      }
    `;
    document.head.appendChild(s);
  }

  // ─── MutationObserver — re-apply hide on every cart drawer re-render ──────

  const observer = new MutationObserver(() => {
    injectStyles();
    // Inline fallback for browsers without :has() support
    document.querySelectorAll(`quantity-selector[data-id^="${FREE_GIFT_VARIANT_ID}"]`).forEach(qs => {
      const item = qs.closest('.product-cart-item');
      if (!item) return;
      const removeLink = item.querySelector('a.remove');
      if (removeLink) removeLink.style.cssText = 'display:none!important;pointer-events:none!important;';
      qs.querySelectorAll('button, input').forEach(el => {
        el.style.pointerEvents = 'none';
        el.style.opacity = '0.4';
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // ─── Block remove link clicks (capture phase, before theme handlers) ──────

  document.addEventListener('click', function (e) {
    const link = e.target.closest('a.remove[data-index], a[href*="/cart/change"][href*="quantity=0"]');
    if (!link) return;

    // Check if this line is the gift item
    fetch('/cart.js').then(r => r.json()).then(cart => {
      const giftItem = cart.items.find(i => i.variant_id === FREE_GIFT_VARIANT_ID);
      if (!giftItem) return;

      const giftLine = cart.items.indexOf(giftItem) + 1;
      const dataIndex = parseInt(link.dataset.index, 10);
      const hrefLine  = link.href ? parseInt(new URL(link.href, location.origin).searchParams.get('line'), 10) : null;

      if (dataIndex === giftLine || hrefLine === giftLine) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showToast();
        // Restore if theme already processed it
        setTimeout(() => {
          _fetch('/cart.js').then(r => r.json()).then(handleCartData);
        }, 200);
      }
    });
  }, true);

  // ─── Toast ────────────────────────────────────────────────────────────────

  function showToast() {
    document.getElementById('fg-toast')?.remove();
    const t = document.createElement('div');
    t.id = 'fg-toast';
    t.textContent = 'Free gift cannot be removed. Spend $100 to keep it!';
    Object.assign(t.style, {
      position: 'fixed', bottom: '24px', left: '50%',
      transform: 'translateX(-50%)',
      background: '#1a1a1a', color: '#fff',
      padding: '12px 20px', borderRadius: '8px',
      fontSize: '14px', fontFamily: 'sans-serif',
      zIndex: '999999', whiteSpace: 'nowrap',
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      pointerEvents: 'none'
    });
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 3500);
  }

  // ─── Init — check cart state on page load ─────────────────────────────────

  _fetch('/cart.js')
    .then(r => r.json())
    .then(cart => handleCartData(cart))
    .catch(e => console.error('[FreeGift] Init error:', e));

  injectStyles();

})();
